import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Search,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Mail,
  MessageSquare,
  Phone,
  ChevronRight,
  X,
  Loader2,
  BarChart3,
  CheckCircle2,
  Clock,
  ArrowLeft,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  getCustomers,
  getCustomerDetail,
  type CustomerInsightSummary,
  type CustomerDetailedInsight,
  getRiskScoreColor,
  getRiskScoreLabel,
} from '@/api/insights';

export function CustomerDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetailedInsight | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Fetch customers
  const { data: customersData, isLoading } = useQuery({
    queryKey: ['customers', projectId],
    queryFn: () => getCustomers(projectId!, 100, 0),
    enabled: !!projectId,
  });

  // Filter customers by search
  const filteredCustomers = customersData?.customers?.filter(customer => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      customer.partner_name.toLowerCase().includes(searchLower) ||
      customer.top_topics.some(t => t.toLowerCase().includes(searchLower))
    );
  }) || [];

  // Handle customer click
  const handleCustomerClick = useCallback(async (customer: CustomerInsightSummary) => {
    if (!projectId) return;
    setIsLoadingDetail(true);
    setIsDetailOpen(true);
    try {
      const detail = await getCustomerDetail(projectId, customer.partner_id);
      setSelectedCustomer(detail);
    } catch (error) {
      console.error('Failed to load customer detail:', error);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [projectId]);

  // Calculate summary stats
  const totalCustomers = customersData?.customers?.length || 0;
  const highRiskCount = customersData?.customers?.filter(c => c.risk_score >= 70).length || 0;
  const avgSentiment = customersData?.customers?.length
    ? customersData.customers.reduce((sum, c) => sum + (c.avg_sentiment_score || 0), 0) / customersData.customers.length
    : 0;

  const getSentimentBadge = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive':
        return <Badge className="bg-green-100 text-green-700">Positive</Badge>;
      case 'negative':
        return <Badge className="bg-red-100 text-red-700">Negative</Badge>;
      case 'mixed':
        return <Badge className="bg-yellow-100 text-yellow-700">Mixed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Neutral</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/project/${projectId}/insights`}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Insights
                </Link>
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              Customer Intelligence
            </h1>
            <p className="text-gray-500 mt-1">
              Aggregated insights across all customer communications
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Customers</p>
                  <p className="text-2xl font-bold">{totalCustomers}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Risk</p>
                  <p className="text-2xl font-bold text-red-600">{highRiskCount}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Sentiment</p>
                  <p className="text-2xl font-bold">{(avgSentiment * 100).toFixed(0)}%</p>
                </div>
                {avgSentiment >= 0.5 ? (
                  <TrendingUp className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Comms</p>
                  <p className="text-2xl font-bold">
                    {customersData?.customers?.reduce((sum, c) => sum + c.total_communications, 0) || 0}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search customers or topics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Customer List */}
        <Card>
          <CardHeader>
            <CardTitle>Customers</CardTitle>
            <CardDescription>
              Showing {filteredCustomers.length} of {totalCustomers} customers, sorted by risk
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No customers found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Import emails or chatter to see customer insights
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.partner_id}
                    onClick={() => handleCustomerClick(customer)}
                    className="flex items-center justify-between py-4 px-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {customer.partner_name}
                        </h3>
                        {getSentimentBadge(customer.overall_sentiment)}
                        <Badge className={cn('text-xs', getRiskScoreColor(customer.risk_score))}>
                          {getRiskScoreLabel(customer.risk_score)} ({customer.risk_score})
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {customer.total_emails}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {customer.total_chatter}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {customer.total_calls}
                        </span>
                        {customer.last_communication && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Last: {new Date(customer.last_communication).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {customer.top_topics.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {customer.top_topics.slice(0, 4).map((topic, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      {customer.open_action_items > 0 && (
                        <div className="flex items-center gap-1 text-orange-600">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="text-sm font-medium">{customer.open_action_items}</span>
                        </div>
                      )}
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Customer Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {isLoadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : selectedCustomer ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  {selectedCustomer.partner_name}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-3">
                  {getSentimentBadge(selectedCustomer.overall_sentiment)}
                  <Badge className={cn(getRiskScoreColor(selectedCustomer.risk_score))}>
                    {getRiskScoreLabel(selectedCustomer.risk_score)} ({selectedCustomer.risk_score})
                  </Badge>
                  <span className="text-gray-400">|</span>
                  <span>{selectedCustomer.total_communications} total communications</span>
                </DialogDescription>
              </DialogHeader>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Mail className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                  <p className="text-lg font-bold">{selectedCustomer.total_emails}</p>
                  <p className="text-xs text-gray-500">Emails</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <MessageSquare className="h-5 w-5 mx-auto mb-1 text-green-500" />
                  <p className="text-lg font-bold">{selectedCustomer.total_chatter}</p>
                  <p className="text-xs text-gray-500">Chatter</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Phone className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                  <p className="text-lg font-bold">{selectedCustomer.total_calls}</p>
                  <p className="text-xs text-gray-500">Calls</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <BarChart3 className="h-5 w-5 mx-auto mb-1 text-orange-500" />
                  <p className="text-lg font-bold">
                    {selectedCustomer.avg_sentiment_score !== null
                      ? `${(selectedCustomer.avg_sentiment_score * 100).toFixed(0)}%`
                      : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">Sentiment</p>
                </div>
              </div>

              {/* Sentiment Distribution */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sentiment Distribution
                </h4>
                <div className="flex gap-1 h-4 rounded overflow-hidden">
                  {selectedCustomer.sentiment_distribution.positive > 0 && (
                    <div
                      className="bg-green-500"
                      style={{
                        width: `${(selectedCustomer.sentiment_distribution.positive / selectedCustomer.total_communications) * 100}%`,
                      }}
                      title={`Positive: ${selectedCustomer.sentiment_distribution.positive}`}
                    />
                  )}
                  {selectedCustomer.sentiment_distribution.neutral > 0 && (
                    <div
                      className="bg-gray-400"
                      style={{
                        width: `${(selectedCustomer.sentiment_distribution.neutral / selectedCustomer.total_communications) * 100}%`,
                      }}
                      title={`Neutral: ${selectedCustomer.sentiment_distribution.neutral}`}
                    />
                  )}
                  {selectedCustomer.sentiment_distribution.mixed > 0 && (
                    <div
                      className="bg-yellow-500"
                      style={{
                        width: `${(selectedCustomer.sentiment_distribution.mixed / selectedCustomer.total_communications) * 100}%`,
                      }}
                      title={`Mixed: ${selectedCustomer.sentiment_distribution.mixed}`}
                    />
                  )}
                  {selectedCustomer.sentiment_distribution.negative > 0 && (
                    <div
                      className="bg-red-500"
                      style={{
                        width: `${(selectedCustomer.sentiment_distribution.negative / selectedCustomer.total_communications) * 100}%`,
                      }}
                      title={`Negative: ${selectedCustomer.sentiment_distribution.negative}`}
                    />
                  )}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Positive ({selectedCustomer.sentiment_distribution.positive})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-400" />
                    Neutral ({selectedCustomer.sentiment_distribution.neutral})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" />
                    Mixed ({selectedCustomer.sentiment_distribution.mixed})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Negative ({selectedCustomer.sentiment_distribution.negative})
                  </span>
                </div>
              </div>

              {/* Topics */}
              {selectedCustomer.all_topics.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Top Topics
                  </h4>
                  <div className="flex gap-2 flex-wrap">
                    {selectedCustomer.all_topics.slice(0, 10).map((t, i) => (
                      <Badge key={i} variant="outline" className="text-sm">
                        {t.topic} ({t.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {selectedCustomer.action_items.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Open Action Items ({selectedCustomer.action_items.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedCustomer.action_items.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded text-sm"
                      >
                        <CheckCircle2 className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-gray-700 dark:text-gray-300">{item.description}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-xs',
                                item.priority === 'high' && 'border-red-300 text-red-700',
                                item.priority === 'medium' && 'border-yellow-300 text-yellow-700',
                                item.priority === 'low' && 'border-gray-300 text-gray-700'
                              )}
                            >
                              {item.priority}
                            </Badge>
                            <span className="text-xs text-gray-400">
                              from {item.source_type}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Communications */}
              {selectedCustomer.recent_communications.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Recent Communications
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedCustomer.recent_communications.map((comm, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        {comm.type === 'email' && <Mail className="h-4 w-4 text-blue-500 mt-0.5" />}
                        {comm.type === 'chatter' && <MessageSquare className="h-4 w-4 text-green-500 mt-0.5" />}
                        {comm.type === 'call' && <Phone className="h-4 w-4 text-purple-500 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {comm.subject || `${comm.type.charAt(0).toUpperCase() + comm.type.slice(1)}`}
                            </span>
                            {comm.sentiment && getSentimentBadge(comm.sentiment)}
                          </div>
                          {comm.summary && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {comm.summary}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(comm.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
